import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { Input } from "@krea/shared/design-system/input/input";
import { AppModal } from "@krea/shared/design-system/modal/app-modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import { toast } from "@krea/shared/design-system/utils/toast";
import { CountryCode } from "@krea/shared/types/common";
import { ssnValidator } from "@krea/shared/utils/ssn-validator";

import { countryCodeToName } from "@/features/common/constants";
import { LenderUserRoleEnum } from "@/features/common/enums";
import { queryKeyMap } from "@/features/query-client";
import { useInviteUserMutation } from "@/features/users/hooks/use-invite-user.mutation";
import type { ILenderUser } from "@/features/users/types";

import { useGetCurrentLenderCompanyQuery } from "../../hooks/use-get-lender-company.query";

const inviteSchema = yup.object().shape({
  ssn: yup
    .string()
    .required("SSN is required")
    .test(
      "country-code",
      "SSN must be valid for the selected country code",
      (value, context) => {
        const countryCode = context.parent.countryCode;

        return ssnValidator(value, countryCode).isValid;
      },
    ),
  role: yup
    .string()
    .required("Role is required")
    .oneOf(Object.values(LenderUserRoleEnum)),
  countryCode: yup
    .string()
    .required("Country code is required")
    .oneOf(Object.values(CountryCode)),
});

type IInviteUserModalFormValues = yup.InferType<typeof inviteSchema>;

interface ITeamHeaderSectionProps {
  onUserInvited?: (user: ILenderUser) => void;
}

export const TeamHeaderSection = ({
  onUserInvited,
}: ITeamHeaderSectionProps) => {
  const { mutate: inviteUser } = useInviteUserMutation();
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const queryClient = useQueryClient();

  const form = useForm<IInviteUserModalFormValues>({
    resolver: yupResolver(inviteSchema),
    mode: "onChange",
    defaultValues: {
      ssn: "",
      role: LenderUserRoleEnum.CREDIT_OFFICER,
      countryCode: lenderCompany?.countryCode || CountryCode.SE,
    },
  });
  const { handleSubmit } = form;

  const onInviteUser = useCallback(() => {
    handleSubmit((data) => {
      if (!lenderCompany) return;
      const usersQueryKey = queryKeyMap.getLenderCompanyUsers(lenderCompany.id);

      inviteUser(
        {
          lenderCompanyId: lenderCompany.id,
          data,
        },
        {
          onSuccess: async () => {
            setIsInviteUserModalOpen(false);
            toast.success(`User ${data.ssn} invited successfully`);
            form.reset();

            await queryClient.invalidateQueries({
              queryKey: usersQueryKey,
            });

            const users =
              queryClient.getQueryData<ILenderUser[]>(usersQueryKey);
            const invitedUser = users?.find((user) => user.ssn === data.ssn);

            if (invitedUser) {
              onUserInvited?.(invitedUser);
            }
          },
          onError: () => {
            toast.error("Failed to invite user");
          },
        },
      );
    })();
  }, [
    handleSubmit,
    lenderCompany,
    inviteUser,
    form,
    queryClient,
    onUserInvited,
  ]);

  return (
    <section>
      <div className="flex flex-col items-center justify-between md:flex-row">
        <div>
          <h1 className="text-page-title">Team Members</h1>
          <p className="my-1 text-helper">
            Manage your team members and their roles.
          </p>
        </div>

        <Button size="lg" onClick={() => setIsInviteUserModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" strokeWidth={3} />
          Add Member
        </Button>
      </div>
      <AppModal
        open={isInviteUserModalOpen}
        setOpen={setIsInviteUserModalOpen}
        title="Add Member"
        onClose={() => setIsInviteUserModalOpen(false)}
        footer={
          <Button
            onClick={onInviteUser}
            disabled={form.formState.isSubmitting || !form.formState.isValid}
          >
            Add
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <ControlledWrapper
            control={form.control}
            name="countryCode"
            label="Country Code"
            render={({ field, fieldState }) => (
              <>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(CountryCode).map((countryCode) => (
                      <SelectItem key={countryCode} value={countryCode}>
                        {countryCodeToName[countryCode]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!fieldState.invalid && (
                  <p className="text-[0.8rem] text-gray-600">
                    Select the country code of the user you want to invite. This
                    will be used to validate the SSN.
                  </p>
                )}
              </>
            )}
          />
          <ControlledWrapper
            control={form.control}
            name="ssn"
            label="SSN"
            render={({ field, fieldState }) => (
              <>
                <Input {...field} />
                {!fieldState.invalid && (
                  <p className="text-[0.8rem] text-gray-600">
                    Enter the SSN of the user you want to invite.
                  </p>
                )}
              </>
            )}
          />
          <ControlledWrapper
            control={form.control}
            name="role"
            label="Role"
            render={({ field, fieldState }) => (
              <>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(LenderUserRoleEnum).map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!fieldState.invalid && (
                  <p className="text-[0.8rem] text-gray-600">
                    Select the role of the user you want to invite.
                  </p>
                )}
              </>
            )}
          />
        </div>
      </AppModal>
    </section>
  );
};
